import { resolveCohortIds } from '@/components/cohorts/cohort-multi-select';
import { DEFAULT_DASHBOARD_TABS } from '@/constants/default-tabs';
import { useMyLearningPaths } from '@/hooks/queries/learning-paths';
import { useMyCohorts } from '@/hooks/queries/cohorts';
import { useTenantConfig } from '@/hooks/queries/tenant-config';
import PromptService from '@/services/prompt-service';
import { useActiveTenant } from '@/hooks/useActiveTenant';
import { useHubTab } from '@/hooks/useHubTab';
import { PromptData, StandardResponse } from '@/types/agent';
import {
  getCategoryStyle,
  getTagGradient,
  normalizeCategory,
} from '@/utils/colors';
import { filterItemsByCohortIds, learningPathsToScenarios } from '@/utils/cohort-filter';
import { Icon } from '@iconify/react';
import { cn, ScrollShadow, Selection } from '@nextui-org/react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useMemo } from 'react';
import DisplayScenarios from '../../pages/layout/components/display-scenarios';

/**
 * Get the appropriate icon for a given tag
 */
const getTabIcon = (tag: string, isActive: boolean) => {
  const normalized = normalizeCategory(tag);
  switch (normalized) {
    case 'Counselor':
      return isActive ? 'lucide:sparkles' : 'lucide:sparkles';
    case 'Action':
      return isActive ? 'lucide:dumbbell' : 'lucide:dumbbell';
    case 'Coach':
      return isActive ? 'lucide:target' : 'lucide:target';
    default:
      return isActive ? 'lucide:sparkles' : 'lucide:sparkles';
  }
};

/**
 * Get short label for mobile tabs
 */
const getShortLabel = (label: string): string => {
  if (label.toLowerCase().includes('inner mastery')) return 'IM Guide';
  if (label.toLowerCase().includes('leadership gym')) return 'Gym';
  if (label.toLowerCase().includes('performance coach')) return 'Coach';
  return label.split(' ')[0];
};

/**
 * Tab descriptions for each category
 */
const getTabDescription = (tag: string): string => {
  const normalized = normalizeCategory(tag);
  switch (normalized) {
    case 'Counselor':
      return 'Discover and activate your core potential';
    case 'Action':
      return 'Practice leadership actions with instant feedback';
    case 'Coach':
      return 'Prepare for upcoming challenges in your workday';
    default:
      return 'Explore leadership tools and resources';
  }
};

const SwipableTabs = ({
  selectedCohortKeys,
}: {
  selectedCohortKeys: Selection;
}) => {
  const { tenantId } = useActiveTenant();
  const { data: tenantConfig } = useTenantConfig(tenantId!);

  // Get configured tabs or fall back to defaults
  const configuredTabs = useMemo(() => {
    const tabs = tenantConfig?.dashboard_config?.tabs;
    if (!tabs || tabs.length === 0) {
      return DEFAULT_DASHBOARD_TABS.map((tab) => ({
        ...tab,
        id: `default-${tab.priority}`,
      }));
    }
    return tabs.sort((a, b) => a.priority - b.priority);
  }, [tenantConfig]);

  const activeTabs = useMemo(
    () => configuredTabs.filter((tab) => tab.is_active),
    [configuredTabs]
  );

  const activeTags = useMemo(
    () => activeTabs.map((tab) => tab.tag),
    [activeTabs]
  );
  const [tag, setTag] = useHubTab(activeTags);
  const activeIndex = Math.max(activeTags.indexOf(tag), 0);

  const { data: cohorts } = useMyCohorts();

  // Query for prompts - same pattern as tabs-section.tsx
  const { data: prompts, isLoading: isLoadingPrompts } = useQuery<
    StandardResponse<PromptData>
  >({
    queryKey: [`agent-${tag}`, { is_active: true, tag }],
    queryFn: async (): Promise<StandardResponse<PromptData>> => {
      const response = await PromptService.getPrompts({
        filters: {
          page: 1,
          is_active: true,
          tag,
        },
      });
      return response as StandardResponse<PromptData>;
    },
  });

  const { data: learningPaths, isLoading: isLoadingLearningPaths } =
    useMyLearningPaths({
      tag,
    });

  const selectedCohortIds = useMemo(
    () => resolveCohortIds(selectedCohortKeys, cohorts?.results ?? []),
    [selectedCohortKeys, cohorts?.results]
  );

  const allCohortIds = useMemo(
    () => cohorts?.results?.map((cohort) => cohort.id) ?? [],
    [cohorts?.results]
  );

  const data = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return learningPathsToScenarios(
        learningPaths?.results ?? [],
        selectedCohortIds,
        allCohortIds
      );
    }

    const promptItems = (prompts?.results || []).map((prompt) => ({
      id: prompt.id,
      name: prompt.name,
      description: prompt.system_prompt || '',
      cohort_ids: prompt.cohort_ids ?? [],
    }));

    return filterItemsByCohortIds(
      promptItems,
      selectedCohortIds,
      allCohortIds
    );
  }, [
    learningPaths?.results,
    prompts?.results,
    selectedCohortIds,
    allCohortIds,
  ]);

  const isLoading = useMemo(() => {
    if (import.meta.env.VITE_LEARNING_PATH_ENABLED === 'true') {
      return isLoadingLearningPaths;
    }
    return isLoadingPrompts;
  }, [isLoadingPrompts, isLoadingLearningPaths]);

  // Get the gradient for the current tab
  const cursorGradient = getTagGradient(tag);
  const currentDescription = getTabDescription(tag);

  const handleTabChange = (index: number) => {
    if (index >= 0 && index < activeTabs.length) {
      const newTag = activeTabs[index]?.tag;
      if (newTag) {
        setTag(newTag);
      }
    }
  };

  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Tabs Header with border */}
      <ScrollShadow
        orientation="horizontal"
        hideScrollBar
        className="w-full mb-4"
      >
        <div className="bg-white rounded-2xl p-1.5 border border-[#E6E6E6] inline-flex min-w-full">
          <div className="flex gap-0 relative w-full">
            {/* Animated background indicator */}
            <motion.div
              className="absolute h-full rounded-xl shadow-[0_4px_12px_rgba(0,0,0,0.15)] z-0"
              style={{
                background: cursorGradient,
                width: `${100 / activeTabs.length}%`,
              }}
              animate={{
                x: `${activeIndex * 100}%`,
              }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />

            {activeTabs.map((tab, index) => {
              const isSelected = activeIndex === index;
              const iconName = getTabIcon(tab.tag, isSelected);
              const shortLabel = getShortLabel(tab.label);
              return (
                <button
                  key={tab.tag}
                  onClick={() => handleTabChange(index)}
                  className={cn(
                    'relative flex-1 flex items-center justify-center gap-1.5 h-11 px-3 rounded-xl transition-colors z-9 min-w-0',
                    isSelected ? 'text-white' : 'text-[#71717a]'
                  )}
                >
                  <Icon
                    icon={iconName}
                    className={cn(
                      'w-4 h-4 flex-shrink-0 transition-colors',
                      isSelected ? 'text-white' : 'text-[#71717a]'
                    )}
                  />
                  <span className="text-sm font-medium truncate">
                    {shortLabel}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </ScrollShadow>

      {/* Tab Description - only show if tabs are not configured */}
      {tenantConfig?.dashboard_config?.tabs?.length === 0 && (
        <motion.p
          key={tag}
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          className={cn('text-sm font-medium mb-4')}
          style={getCategoryStyle(tag)}
        >
          {currentDescription}
        </motion.p>
      )}

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        <DisplayScenarios
          tasks={data}
          isLoading={isLoading}
          tag={tag}
          selectedCohortIds={selectedCohortIds}
        />
      </div>
    </div>
  );
};

export default SwipableTabs;
